import { useState, type FormEvent } from "react";
import { useToast, useColorModeValue } from "@chakra-ui/react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  Button, FormControl, FormLabel, Input, VStack, Checkbox, SimpleGrid,
} from "@chakra-ui/react";
import { adminApi } from "../../../data/axios/adminApi";
import { AdminRole } from "../../../types/admin";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  availableRoles: AdminRole[];
  onSuccess: () => void;
}

export function UserCreateModal({ isOpen, onClose, availableRoles, onSuccess }: Props) {
  const toast = useToast();

  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const labelColor  = useColorModeValue("#434347", "#e8edf2");
  const cardBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const inputBg     = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const inputBorder = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setEmail("");
    setName("");
    setSelectedRoleIds([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const toggleRole = (id: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(id) ? prev.filter((rid) => rid !== id) : [...prev, id],
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await adminApi.createUser({
        email,
        name,
        country: "BR",
        roles: selectedRoleIds,
      });
      toast({ title: "Usuário cadastrado com sucesso", status: "success", duration: 3000, isClosable: true });
      onSuccess();
      handleClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast({
        title: detail ?? "Erro ao cadastrar usuário",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit} borderRadius="2xl" mx={4}>
        <ModalHeader
          fontSize="lg"
          fontWeight={600}
          color={titleColor}
          borderBottomWidth="1px"
          borderColor={cardBorder}
          pb={4}
        >
          Cadastrar Usuário
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pt={5}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>E-mail</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colaborador@empresa.com"
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Nome Completo</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
            </FormControl>

            {availableRoles.length > 0 && (
              <FormControl>
                <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={2}>Papéis</FormLabel>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                  {availableRoles.map((role) => (
                    <Checkbox
                      key={role.id}
                      isChecked={selectedRoleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                      colorScheme="blue"
                      size="sm"
                    >
                      <span style={{ fontSize: "14px", color: labelColor }}>{role.name}</span>
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </FormControl>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={cardBorder} gap={2} pt={4}>
          <Button variant="ghost" borderRadius="50px" size="sm" onClick={handleClose}>Cancelar</Button>
          <Button
            bg="#009efb"
            color="white"
            _hover={{ bg: "#007bd4" }}
            borderRadius="50px"
            size="sm"
            type="submit"
            isLoading={submitting}
          >
            Cadastrar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
